// Counts (spec/13 §6.4 Count; `inventory:count`, or `inventory:supplies` for supplies): a **full** count lists every
// active item — the first thing to do after stock is turned on, and the month-end routine — and a **spot** count the
// items chosen (the low ones, a fridge after a power cut). Starting one opens it on its own page, where it is counted
// shelf by shelf and kept on the server as it goes (`CountPage.tsx`). The Counts tab lists the open count first, then
// the past ones with what they found.

import type { StockCountSummary, StockItem } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, Checkbox, Dialog, Table, TextField, useToast, type TableColumn } from '@clavian/ui-web';
import { useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent, type ReactNode } from 'react';

import { LoadFailure } from '../../shell/LoadFailure';
import { useCurrentVenue } from '../../shell/scope';
import { Notice, SearchField, TableSkeleton } from '../../ui';
import type { StockAccess } from './access';
import { useStockFormat } from './format';
import { LevelBadge } from './parts';
import { useCreateCount, useStockCounts, useStockItems } from './queries';
import { StockEmpty, useWhen, type StockDialog } from './StockPage';
import { useProblem } from '../../api/use-problem';

const STATUS_TONE = { open: 'accent', submitted: 'ok', cancelled: 'neutral' } as const;

export function CountsTab({ access, onStart }: { readonly access: StockAccess; readonly onStart: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const navigate = useNavigate();
  const stock = useStockFormat();
  const when = useWhen();
  const counts = useStockCounts(venue.organizationId, venue.id);
  const rows = (counts.data?.pages ?? []).flatMap((page) => page.data);
  const openCount = (count: StockCountSummary) => void navigate({ to: '/v/$venueSlug/stock/counts/$countId', params: { venueSlug: venue.slug, countId: count.id } });

  const columns: TableColumn<StockCountSummary>[] = [
    {
      key: 'started',
      header: t('stock.counts.started'),
      cell: (count) => (
        <button type="button" className="console-cell-link" onClick={() => openCount(count)}>
          <span>{t(`stock.counts.kind.${count.kind}`)}</span>
          <span className="console-cell-sub">{t('stock.counts.startedBy', { when: when(count.startedAt), who: count.startedByName ?? t('stock.history.system') })}</span>
        </button>
      ),
    },
    {
      key: 'status',
      header: t('stock.counts.status'),
      cell: (count) => (
        <Badge tone={STATUS_TONE[count.status]} dot={count.status === 'open'}>
          {t(`stock.counts.statusWord.${count.status}`)}
        </Badge>
      ),
    },
    { key: 'progress', header: t('stock.counts.progress'), numeric: true, cell: (count) => <span className="console-num">{t('stock.counts.progressOf', { counted: count.countedCount, total: count.lineCount })}</span> },
    ...(access.reports
      ? [{ key: 'variance', header: t('stock.counts.variance'), numeric: true, cell: (count: StockCountSummary) => <span className="console-money">{stock.money(count.varianceValue)}</span> }]
      : []),
    {
      key: 'action',
      header: <span className="clav-visually-hidden">{t('common.actions')}</span>,
      cell: (count) => (
        <Button size="sm" variant={count.status === 'open' ? 'secondary' : 'ghost'} onClick={() => openCount(count)}>
          {count.status === 'open' ? t('stock.counts.continue') : t('stock.counts.view')}
        </Button>
      ),
    },
  ];

  let body: ReactNode;
  if (counts.data === undefined) {
    body = counts.isError ? <LoadFailure error={counts.error} message={t('stock.counts.unavailable')} onRetry={() => void counts.refetch()} /> : <TableSkeleton />;
  } else if (rows.length === 0) {
    body = (
      <StockEmpty
        title={t('stock.counts.emptyTitle')}
        description={t('stock.counts.empty')}
        action={
          access.count ? (
            <Button variant="primary" onClick={onStart}>
              {t('stock.actions.count')}
            </Button>
          ) : undefined
        }
      />
    );
  } else {
    body = (
      <>
        <div className="console-table-card">
          <Table caption={t('stock.counts.caption')} captionHidden columns={columns} rows={rows} rowKey={(count) => count.id} />
        </div>
        {counts.hasNextPage ? (
          <div className="console-list-foot">
            <Button variant="secondary" size="sm" busy={counts.isFetchingNextPage} onClick={() => void counts.fetchNextPage()}>
              {t('stock.history.more')}
            </Button>
          </div>
        ) : null}
      </>
    );
  }
  return <div className="console-stock-tab">{body}</div>;
}

// -------------------------------------------------------------------------------------------------
// Starting a count
// -------------------------------------------------------------------------------------------------

export function StartCountDialog({ dialog, access, onClose }: { readonly dialog: Extract<StockDialog, { kind: 'count' }> | null; readonly access: StockAccess; readonly onClose: () => void }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Dialog open={dialog !== null} onOpenChange={(open) => (open ? undefined : onClose())} size="md" dirty="auto" title={t('stock.start.title')} description={t('stock.start.hint')}>
      {dialog !== null ? <StartCountForm start={dialog} access={access} onDone={onClose} /> : null}
    </Dialog>
  );
}

function StartCountForm({ start, access, onDone }: { readonly start: Extract<StockDialog, { kind: 'count' }>; readonly access: StockAccess; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const venue = useCurrentVenue();
  const navigate = useNavigate();
  const when = useWhen();
  const items = useStockItems(venue.organizationId, venue.id);
  const counts = useStockCounts(venue.organizationId, venue.id);
  const create = useCreateCount(venue.organizationId, venue.id);
  const suppliesOnly = access.countSuppliesOnly;
  const countable = (items.data ?? []).filter((item) => !item.archived && (!suppliesOnly || item.kind === 'supply'));
  const [kind, setKind] = useState<'full' | 'spot'>(start.item !== undefined || suppliesOnly ? 'spot' : 'full');
  const [chosen, setChosen] = useState<ReadonlySet<string>>(() => new Set(start.item === undefined ? (suppliesOnly ? countable.map((item) => item.id) : []) : [start.item.id]));
  const [query, setQuery] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const open = (counts.data?.pages ?? []).flatMap((page) => page.data).find((count) => count.status === 'open');

  const go = (countId: string) => void navigate({ to: '/v/$venueSlug/stock/counts/$countId', params: { venueSlug: venue.slug, countId } });

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (kind === 'spot' && chosen.size === 0) {
      setError(t('stock.start.chooseItems'));
      return;
    }
    setError(null);
    create.mutate(
      { kind, ...(note.trim() === '' ? {} : { note: note.trim() }), ...(kind === 'spot' ? { stockItemIds: [...chosen] } : {}) },
      {
        onSuccess: (count) => {
          show({ tone: 'ok', title: t('stock.start.started', { count: count.lineCount }) });
          onDone();
          go(count.id);
        },
        onError: (failure) => setError(problem.text(failure, { fallback: t('stock.start.refused') })),
      },
    );
  }

  const needle = query.trim().toLowerCase();
  const listed = countable.filter((item) => needle === '' || item.name.toLowerCase().includes(needle));
  const toggle = (item: StockItem, on: boolean) =>
    setChosen((current) => {
      const next = new Set(current);
      if (on) next.add(item.id);
      else next.delete(item.id);
      return next;
    });

  return (
    <form className="console-form" aria-label={t('stock.start.title')} noValidate onSubmit={submit}>
      {open !== undefined ? (
        <Notice
          tone="info"
          title={t('stock.start.openTitle')}
          action={
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => {
                onDone();
                go(open.id);
              }}
            >
              {t('stock.counts.continue')}
            </Button>
          }
        >
          <p>{t('stock.start.openBody', { kind: t(`stock.counts.kind.${open.kind}`), when: when(open.startedAt), counted: open.countedCount, total: open.lineCount })}</p>
        </Notice>
      ) : null}
      {error !== null ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
      <fieldset className="console-stock-choice">
        <legend className="clav-field__label">{t('stock.start.kind')}</legend>
        <div className="console-stock-choice__grid">
          {(['full', 'spot'] as const).map((value) => {
            const disabled = value === 'full' && suppliesOnly;
            return (
              <label key={value} className={kind === value ? 'console-stock-choice__tile is-checked' : 'console-stock-choice__tile'} aria-disabled={disabled || undefined}>
                <input type="radio" name="count-kind" value={value} checked={kind === value} disabled={disabled} onChange={() => setKind(value)} className="clav-visually-hidden" />
                <span className="console-stock-choice__title">{t(`stock.start.${value}.title`)}</span>
                <span className="console-stock-choice__body">{value === 'full' ? t('stock.start.full.body', { count: countable.length }) : t('stock.start.spot.body')}</span>
              </label>
            );
          })}
        </div>
      </fieldset>
      {kind === 'spot' ? (
        <fieldset className="console-stock-spot">
          <legend className="clav-field__label">{t('stock.start.items', { count: chosen.size })}</legend>
          <div className="console-stock-spot__tools">
            <SearchField label={t('stock.start.search')} value={query} onChange={(event) => setQuery(event.target.value)} />
            <Button type="button" size="sm" variant="ghost" onClick={() => setChosen(new Set(countable.filter((item) => item.level !== 'ok').map((item) => item.id)))}>
              {t('stock.start.pickAttention')}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setChosen(new Set())}>
              {t('stock.start.clear')}
            </Button>
          </div>
          <ul className="console-stock-spot__list">
            {listed.map((item) => (
              <li key={item.id} className="console-stock-spot__row">
                <Checkbox label={item.name} checked={chosen.has(item.id)} onCheckedChange={(checked) => toggle(item, checked === true)} />
                {item.level === 'ok' ? null : <LevelBadge level={item.level} />}
              </li>
            ))}
          </ul>
        </fieldset>
      ) : null}
      <TextField label={t('stock.start.note')} showOptional autoComplete="off" value={note} placeholder={t('stock.start.notePlaceholder')} onChange={(event) => setNote(event.target.value)} />
      <div className="console-form__actions console-form__actions--end">
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary" busy={create.isPending}>
          {kind === 'full' ? t('stock.start.startFull', { count: countable.length }) : t('stock.start.startSpot', { count: chosen.size })}
        </Button>
      </div>
    </form>
  );
}
